import React, { useState, useEffect } from "react";
import { X, Save, Download, Printer, FileText, CheckCircle2, Building, User, Calendar, Check, PenTool, RotateCcw } from "lucide-react";
import { exportAgreementToWord, printAgreement } from "../../utils/agreementExport";

export default function PartnerAgreementModal({
  agreement,
  partners = [],
  templates = [],
  companies = [],
  companyLogo = "",
  onClose,
  onSave
}) {
  const isNew = !agreement || !agreement.id;

  // Selected template
  const defaultTemplate = templates.find((t) => t.isDefault) || templates[0] || null;
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    agreement?.templateId || defaultTemplate?.id || ""
  );

  const currentTemplate = templates.find((t) => t.id === selectedTemplateId) || defaultTemplate;

  // Selected Prime Company
  const defaultPrime = companies.find((c) => c.isActive) || companies[0] || null;
  const [primeCompanyId, setPrimeCompanyId] = useState(
    agreement?.primeCompanyId || defaultPrime?.id || ""
  );
  const [primeCompanyName, setPrimeCompanyName] = useState(
    agreement?.primeCompanyName || defaultPrime?.name || "Longarm Technologies, LLC"
  );
  const [primeCompanyLogo, setPrimeCompanyLogo] = useState(
    agreement?.primeCompanyLogo || defaultPrime?.url || companyLogo || ""
  );
  const [primeCompanyState, setPrimeCompanyState] = useState(
    agreement?.primeCompanyState || defaultPrime?.state || "Nevada"
  );
  const [primeCompanyAddress, setPrimeCompanyAddress] = useState(
    agreement?.primeCompanyAddress ||
    [defaultPrime?.address, defaultPrime?.city, defaultPrime?.state, defaultPrime?.zip].filter(Boolean).join(", ") ||
    "1543 Frew mill Rd News Castle PA"
  );
  const [primeCompanyAcronym, setPrimeCompanyAcronym] = useState(
    agreement?.primeCompanyAcronym || (primeCompanyName.split(" ").map(w => w[0]).join("").toUpperCase()) || "LAT"
  );

  // Active form subtab
  const [activeTab, setActiveTab] = useState("parties"); // "parties", "roles", "clauses", "signatories"
  const [isSaving, setIsSaving] = useState(false);

  // Form State - Partner
  const [partnerId, setPartnerId] = useState(agreement?.partnerId || "");
  const [partnerCompany, setPartnerCompany] = useState(agreement?.partnerCompany || "");
  const [partnerState, setPartnerState] = useState(agreement?.partnerState || "Maryland");
  const [partnerAddress, setPartnerAddress] = useState(agreement?.partnerAddress || "");
  const [partnerAcronym, setPartnerAcronym] = useState(agreement?.partnerAcronym || "");
  const [partnerPocName, setPartnerPocName] = useState(agreement?.partnerPocName || "");
  const [partnerPocEmail, setPartnerPocEmail] = useState(agreement?.partnerPocEmail || "");
  const [partnerPocPhone, setPartnerPocPhone] = useState(agreement?.partnerPocPhone || "");

  const [title, setTitle] = useState(agreement?.title || "");
  const [status, setStatus] = useState(agreement?.status || "Draft");
  const [effectiveDate, setEffectiveDate] = useState(
    agreement?.effectiveDate || new Date().toISOString().split("T")[0]
  );
  const [customerName, setCustomerName] = useState(
    agreement?.customerName || "US Government Department of Veterans Affairs"
  );
  const [customerAcronym, setCustomerAcronym] = useState(
    agreement?.customerAcronym || "VA"
  );
  const [procurementDescription, setProcurementDescription] = useState(
    agreement?.procurementDescription || "leased space and the material and services required for the buildout of said leased space"
  );

  const [leadProposalParty, setLeadProposalParty] = useState(
    agreement?.leadProposalParty || "USPAS"
  );
  const [clientInterfaceParty, setClientInterfaceParty] = useState(
    agreement?.clientInterfaceParty || primeCompanyAcronym || "LAT"
  );

  const [noticesLatContact, setNoticesLatContact] = useState(
    agreement?.noticesLatContact ||
    `${primeCompanyName}\n${primeCompanyAddress}\nAttention: Dale Flippo/James Pasqualini\nPhone: (866) 668-6060\nemail: support@longarmtechnologies.com`
  );

  const [noticesPartnerContact, setNoticesPartnerContact] = useState(
    agreement?.noticesPartnerContact || ""
  );

  const [signatoryLat, setSignatoryLat] = useState(
    agreement?.signatoryLat || {
      company: primeCompanyName,
      signatoryName: "Dale Flippo / James Pasqualini",
      signatoryTitle: "President / Vice President",
      date: effectiveDate,
      signatureAdopted: false,
      signatureText: "Dale Flippo",
      signedAt: null
    }
  );

  const [signatoryPartner, setSignatoryPartner] = useState(
    agreement?.signatoryPartner || {
      company: "",
      signatoryName: "",
      signatoryTitle: "Authorized Representative",
      date: "",
      signatureAdopted: false,
      signatureText: "",
      signedAt: null
    }
  );

  const [sections, setSections] = useState(
    agreement?.sections || currentTemplate?.sections || []
  );

  // When Prime Company is selected
  const handlePrimeCompanySelect = (compId) => {
    setPrimeCompanyId(compId);
    const comp = companies.find((c) => c.id === compId || c.docId === compId);
    if (comp) {
      const cName = comp.name || "Our Company";
      const fullAddr = [comp.address, comp.city, comp.state, comp.zip].filter(Boolean).join(", ") || comp.address || "";
      const acronym = cName.split(" ").map(w => w[0]).join("").toUpperCase() || "LAT";
      
      setPrimeCompanyName(cName);
      setPrimeCompanyLogo(comp.url || companyLogo || "");
      setPrimeCompanyState(comp.state || "Nevada");
      setPrimeCompanyAddress(fullAddr);
      setPrimeCompanyAcronym(acronym);
      setClientInterfaceParty(acronym);

      setNoticesLatContact(
        `${cName}\n${fullAddr}\nAttention: ${signatoryLat.signatoryName || "Dale Flippo/James Pasqualini"}\nPhone: (866) 668-6060\nemail: support@longarmtechnologies.com`
      );

      setSignatoryLat((prev) => ({
        ...prev,
        company: cName
      }));

      if (!title || isNew) {
        setTitle(`${currentTemplate?.title || "Teaming Agreement"} - ${cName} & ${partnerCompany || "Partner"} (${customerAcronym || "VA"})`);
      }
    }
  };

  // When partner is selected, auto-fill details
  const handlePartnerSelect = (pId) => {
    setPartnerId(pId);
    const selectedP = partners.find((p) => p.id === pId);
    if (selectedP) {
      const pName = selectedP.name || "";
      setPartnerCompany(pName);
      if (!title || isNew) {
        setTitle(`${currentTemplate?.title || "Teaming Agreement"} - ${primeCompanyName} & ${pName} (${customerAcronym || "VA"})`);
      }
      if (selectedP.address) setPartnerAddress(selectedP.address);
      if (selectedP.state) setPartnerState(selectedP.state);

      const primaryPoc = (selectedP.pocs && selectedP.pocs[0]) || {};
      if (primaryPoc.name) setPartnerPocName(primaryPoc.name);
      if (primaryPoc.email) setPartnerPocEmail(primaryPoc.email);
      if (primaryPoc.phone) setPartnerPocPhone(primaryPoc.phone);

      const acronym = pName.split(" ").map((w) => w[0]).join("").toUpperCase() || "Partner";
      setPartnerAcronym(acronym);
      setLeadProposalParty(acronym);

      const templatePartnerNotice = currentTemplate?.defaultPartnerNotices || `{{partnerCompany}}\n{{partnerAddress}}\nAttention: {{partnerPocName}}\nPhone: {{partnerPocPhone}}\nemail: {{partnerPocEmail}}`;
      const resolvedPartnerNotice = templatePartnerNotice
        .replace(/{{partnerCompany}}/g, pName)
        .replace(/{{partnerAddress}}/g, selectedP.address || "Address")
        .replace(/{{partnerPocName}}/g, primaryPoc.name || "Authorized Representative")
        .replace(/{{partnerPocPhone}}/g, primaryPoc.phone || "N/A")
        .replace(/{{partnerPocEmail}}/g, primaryPoc.email || "N/A");

      setNoticesPartnerContact(resolvedPartnerNotice);

      setSignatoryPartner((prev) => ({
        ...prev,
        company: pName,
        signatoryName: primaryPoc.name || "",
        signatureText: primaryPoc.name || ""
      }));
    }
  };

  // When template changes in new mode, load template sections
  const handleTemplateChange = (tId) => {
    setSelectedTemplateId(tId);
    const tpl = templates.find((t) => t.id === tId);
    if (tpl && isNew) {
      setSections(tpl.sections || []);
      if (tpl.defaultLatNotices) {
        setNoticesLatContact(
          tpl.defaultLatNotices
            .replace(/Longarm Technologies, LLC/g, primeCompanyName)
            .replace(/1543 Frew mill Rd News Castle PA/g, primeCompanyAddress)
        );
      }
      if (tpl.defaultPartnerNotices && partnerCompany) {
        const resolved = tpl.defaultPartnerNotices
          .replace(/{{partnerCompany}}/g, partnerCompany)
          .replace(/{{partnerAddress}}/g, partnerAddress || "Address")
          .replace(/{{partnerPocName}}/g, partnerPocName || "Authorized Representative")
          .replace(/{{partnerPocPhone}}/g, partnerPocPhone || "N/A")
          .replace(/{{partnerPocEmail}}/g, partnerPocEmail || "N/A");
        setNoticesPartnerContact(resolved);
      }
      if (!title) {
        setTitle(`${tpl.title} - ${primeCompanyName} & ${partnerCompany || "Partner"}`);
      }
    }
  };

  // Digital Signature Handlers
  const handleAdoptLatSignature = () => {
    const textToSign = signatoryLat.signatureText || signatoryLat.signatoryName || "Authorized Signatory";
    setSignatoryLat({
      ...signatoryLat,
      signatureAdopted: true,
      signatureText: textToSign,
      date: signatoryLat.date || effectiveDate,
      signedAt: new Date().toISOString()
    });
  };

  const handleClearLatSignature = () => {
    setSignatoryLat({
      ...signatoryLat,
      signatureAdopted: false,
      signedAt: null
    });
  };

  const handleAdoptPartnerSignature = () => {
    const textToSign = signatoryPartner.signatureText || signatoryPartner.signatoryName || "Authorized Signatory";
    setSignatoryPartner({
      ...signatoryPartner,
      signatureAdopted: true,
      signatureText: textToSign,
      date: signatoryPartner.date || effectiveDate,
      signedAt: new Date().toISOString()
    });
  };

  const handleClearPartnerSignature = () => {
    setSignatoryPartner({
      ...signatoryPartner,
      signatureAdopted: false,
      signedAt: null
    });
  };

  const getExportData = () => ({
    title: title || currentTemplate?.title || "Teaming Agreement",
    status,
    effectiveDate,
    primeCompanyId,
    primeCompanyName,
    primeCompanyLogo,
    primeCompanyState,
    primeCompanyAddress,
    primeCompanyAcronym,
    partnerId,
    partnerCompany,
    partnerState,
    partnerAddress,
    partnerAcronym,
    partnerPocName,
    partnerPocEmail,
    partnerPocPhone,
    customerName,
    customerAcronym,
    procurementDescription,
    leadProposalParty,
    clientInterfaceParty,
    noticesLatContact,
    noticesPartnerContact,
    signatoryLat,
    signatoryPartner,
    sections
  });

  const handleExportWord = () => {
    exportAgreementToWord(getExportData(), currentTemplate, primeCompanyLogo || companyLogo);
  };

  const handlePrint = () => {
    printAgreement(getExportData(), currentTemplate, primeCompanyLogo || companyLogo);
  };

  const handleSaveSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      alert("Please provide an agreement title.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        templateId: selectedTemplateId,
        templateTitle: currentTemplate?.title || "Teaming Agreement",
        title,
        status,
        effectiveDate,
        primeCompanyId,
        primeCompanyName,
        primeCompanyLogo,
        primeCompanyState,
        primeCompanyAddress,
        primeCompanyAcronym,
        partnerId,
        partnerName: partnerCompany,
        partnerCompany,
        partnerState,
        partnerAddress,
        partnerAcronym,
        partnerPocName,
        partnerPocEmail,
        partnerPocPhone,
        customerName,
        customerAcronym,
        procurementDescription,
        leadProposalParty,
        clientInterfaceParty,
        noticesLatContact,
        noticesPartnerContact,
        signatoryLat,
        signatoryPartner,
        sections
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      console.error("Error saving agreement:", err);
      alert("Failed to save agreement: " + (err.message || err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
        padding: "1rem"
      }}
      onClick={onClose}
    >
      <div
        className="card glass-card"
        style={{
          width: "100%",
          maxWidth: "980px",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
          overflow: "hidden",
          padding: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: "1rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(10, 12, 18, 0.4)",
          flexWrap: "wrap",
          gap: "0.75rem"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <FileText size={20} style={{ color: "var(--primary)" }} />
            <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "var(--text-primary)" }}>
              {isNew ? "Create Partner Agreement" : `Edit Agreement: ${title}`}
            </h3>

            {/* Quick Export Buttons */}
            <div style={{ display: "flex", gap: "0.4rem", marginLeft: "0.5rem" }}>
              <button
                type="button"
                onClick={handlePrint}
                className="btn btn-secondary"
                style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}
                title="Print / PDF Preview"
              >
                <Printer size={13} /> Print / PDF
              </button>
              <button
                type="button"
                onClick={handleExportWord}
                className="btn btn-secondary"
                style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}
                title="Export to Word (.doc)"
              >
                <Download size={13} /> Export Word (.doc)
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Top Controls Bar: Our Company, Template Selector, Partner, Status */}
        <div style={{
          padding: "0.75rem 1.5rem",
          backgroundColor: "rgba(255, 255, 255, 0.02)",
          borderBottom: "1px solid var(--border-color)",
          display: "grid",
          gridTemplateColumns: "1.3fr 1.3fr 1.3fr 0.9fr",
          gap: "0.75rem"
        }}>
          {/* Prime Company Selector */}
          <div>
            <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.2rem", color: "var(--primary)", fontWeight: 600 }}>
              Our Company (Prime)
            </label>
            <select
              className="form-input"
              value={primeCompanyId}
              onChange={(e) => handlePrimeCompanySelect(e.target.value)}
              style={{ margin: 0, padding: "0.35rem 0.6rem", fontSize: "0.85rem", borderColor: "var(--primary)" }}
            >
              {companies.length === 0 ? (
                <option value="">Longarm Technologies, LLC (Default)</option>
              ) : (
                companies.map((c) => (
                  <option key={c.id || c.docId} value={c.id || c.docId}>
                    {c.name} {c.isActive ? "(Default)" : ""}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Template Selector */}
          <div>
            <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.2rem" }}>
              Agreement Template
            </label>
            <select
              className="form-input"
              value={selectedTemplateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              style={{ margin: 0, padding: "0.35rem 0.6rem", fontSize: "0.85rem" }}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} {t.isDefault ? "(Default)" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Linked Partner */}
          <div>
            <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.2rem" }}>
              Linked Partner Organization
            </label>
            <select
              className="form-input"
              value={partnerId}
              onChange={(e) => handlePartnerSelect(e.target.value)}
              style={{ margin: 0, padding: "0.35rem 0.6rem", fontSize: "0.85rem" }}
            >
              <option value="">-- Select Partner (or custom) --</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="form-label" style={{ fontSize: "0.75rem", marginBottom: "0.2rem" }}>
              Status
            </label>
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ margin: 0, padding: "0.35rem 0.6rem", fontSize: "0.85rem" }}
            >
              <option value="Draft">Draft</option>
              <option value="Under Review">Under Review</option>
              <option value="Executed">Executed</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Subtabs Bar */}
        <div style={{
          display: "flex",
          borderBottom: "1px solid var(--border-color)",
          backgroundColor: "rgba(10, 12, 18, 0.2)",
          padding: "0 1.5rem"
        }}>
          {[
            { id: "parties", label: "1. Parties & Customer" },
            { id: "roles", label: "2. Roles & Notices" },
            { id: "clauses", label: `3. Clauses & Terms (${sections.length})` },
            { id: "signatories", label: "4. Digital Signatures" }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "0.75rem 1rem",
                border: "none",
                background: "none",
                borderBottom: activeTab === tab.id ? "2px solid var(--primary)" : "2px solid transparent",
                color: activeTab === tab.id ? "var(--primary)" : "var(--text-secondary)",
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: "0.85rem",
                cursor: "pointer"
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Body */}
        <div style={{ padding: "1.5rem", overflowY: "auto", flex: 1 }}>
          <form id="partnerAgreementForm" onSubmit={handleSaveSubmit}>
            {/* Title & Date */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontWeight: 600 }}>Agreement Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Teaming Agreement - Royal Oaks & Partner"
                  required
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontWeight: 600 }}>Effective Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* TAB 1: Parties & Customer */}
            {activeTab === "parties" && (
              <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                {/* Prime Entity Info */}
                <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)", backgroundColor: "rgba(59, 130, 246, 0.02)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                    <h4 style={{ margin: 0, color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                      Prime Contractor Information ({primeCompanyName})
                    </h4>
                    {primeCompanyLogo && (
                      <img src={primeCompanyLogo} alt="Logo" style={{ maxHeight: "28px", maxWidth: "120px", objectFit: "contain" }} />
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: "0.75rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Prime Legal Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={primeCompanyName}
                        onChange={(e) => setPrimeCompanyName(e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>State of Incorporation</label>
                      <input
                        type="text"
                        className="form-input"
                        value={primeCompanyState}
                        onChange={(e) => setPrimeCompanyState(e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Short Name / Acronym</label>
                      <input
                        type="text"
                        className="form-input"
                        value={primeCompanyAcronym}
                        onChange={(e) => setPrimeCompanyAcronym(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: "0.75rem 0 0 0" }}>
                    <label className="form-label" style={{ fontSize: "0.75rem" }}>Prime Office Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={primeCompanyAddress}
                      onChange={(e) => setPrimeCompanyAddress(e.target.value)}
                    />
                  </div>
                </div>

                {/* Partner Details */}
                <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)" }}>
                  <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                    Partner Entity Information
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: "0.75rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Partner Legal Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={partnerCompany}
                        onChange={(e) => setPartnerCompany(e.target.value)}
                        placeholder="e.g. Apex Defense Solutions, Inc."
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>State of Incorporation</label>
                      <input
                        type="text"
                        className="form-input"
                        value={partnerState}
                        onChange={(e) => setPartnerState(e.target.value)}
                        placeholder="e.g. Maryland, Virginia..."
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Short Name / Acronym</label>
                      <input
                        type="text"
                        className="form-input"
                        value={partnerAcronym}
                        onChange={(e) => setPartnerAcronym(e.target.value)}
                        placeholder="e.g. USPAS, Apex"
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: "0.75rem 0 0 0" }}>
                    <label className="form-label" style={{ fontSize: "0.75rem" }}>Partner Office Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={partnerAddress}
                      onChange={(e) => setPartnerAddress(e.target.value)}
                      placeholder="e.g. 1234 Technology Parkway, Suite 100, Bethesda, MD 20814"
                    />
                  </div>
                </div>

                {/* Customer / Procurement Details */}
                <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)" }}>
                  <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                    Target Customer & Procurement Scope
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.75rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Customer / Agency Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="e.g. US Government Department of Veterans Affairs"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Customer Acronym</label>
                      <input
                        type="text"
                        className="form-input"
                        value={customerAcronym}
                        onChange={(e) => setCustomerAcronym(e.target.value)}
                        placeholder="e.g. VA, DoD, DHA"
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: "0.75rem 0 0 0" }}>
                    <label className="form-label" style={{ fontSize: "0.75rem" }}>Procurement Scope / Description</label>
                    <textarea
                      className="form-input"
                      rows={2}
                      value={procurementDescription}
                      onChange={(e) => setProcurementDescription(e.target.value)}
                      placeholder="e.g. leased space and the material and services required for the buildout of said leased space"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: Roles & Notices */}
            {activeTab === "roles" && (
              <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                {/* Roles Allocation */}
                <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)" }}>
                  <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                    Proposal & Interface Responsibilities
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>
                        Lead Proposal Writing Party (Section 5)
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={leadProposalParty}
                        onChange={(e) => setLeadProposalParty(e.target.value)}
                        placeholder="e.g. USPAS or Partner Name"
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>
                        Customer Main Contact Interface (Section 9/10)
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={clientInterfaceParty}
                        onChange={(e) => setClientInterfaceParty(e.target.value)}
                        placeholder="e.g. LAT, Royal Oaks"
                      />
                    </div>
                  </div>
                </div>

                {/* Notices Section 18 */}
                <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)" }}>
                  <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                    Notices & Points of Contact (Section 18)
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>
                        {primeCompanyName} Notice Details
                      </label>
                      <textarea
                        className="form-input"
                        rows={5}
                        value={noticesLatContact}
                        onChange={(e) => setNoticesLatContact(e.target.value)}
                        style={{ fontSize: "0.8rem", lineHeight: 1.35 }}
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>
                        {partnerCompany || "Partner"} Notice Details
                      </label>
                      <textarea
                        className="form-input"
                        rows={5}
                        value={noticesPartnerContact}
                        onChange={(e) => setNoticesPartnerContact(e.target.value)}
                        placeholder="Partner Company&#10;Address&#10;Attention: Contact Name&#10;Phone: ...&#10;email: ..."
                        style={{ fontSize: "0.8rem", lineHeight: 1.35 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: Clauses & Terms */}
            {activeTab === "clauses" && (
              <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", margin: "0 0 0.5rem 0" }}>
                  All {sections.length} sections from the template are loaded below. You can customize any clause specific to this partner agreement.
                </p>
                {sections.map((sec, idx) => (
                  <div
                    key={sec.id || idx}
                    style={{
                      padding: "0.75rem 1rem",
                      border: "1px solid var(--border-color)",
                      borderRadius: "6px",
                      backgroundColor: "rgba(255, 255, 255, 0.01)"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
                      <span style={{ fontWeight: 700, color: "var(--primary)", fontSize: "0.85rem" }}>
                        {sec.sectionNumber || idx + 1}.
                      </span>
                      <input
                        type="text"
                        className="form-input"
                        value={sec.title || ""}
                        onChange={(e) => {
                          const next = [...sections];
                          next[idx] = { ...next[idx], title: e.target.value };
                          setSections(next);
                        }}
                        style={{ margin: 0, padding: "0.2rem 0.5rem", fontSize: "0.85rem", fontWeight: 600, maxWidth: "350px" }}
                      />
                    </div>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={sec.content || ""}
                      onChange={(e) => {
                        const next = [...sections];
                        next[idx] = { ...next[idx], content: e.target.value };
                        setSections(next);
                      }}
                      style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* TAB 4: Digital Signatures */}
            {activeTab === "signatories" && (
              <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
                  {/* Prime Signatory */}
                  <div className="card glass-card" style={{ padding: "1.25rem", border: "1px solid var(--border-color)", backgroundColor: "rgba(59, 130, 246, 0.02)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                      <h4 style={{ margin: 0, color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                        {primeCompanyName} Signatory
                      </h4>
                      {signatoryLat.signatureAdopted ? (
                        <span className="badge badge-success" style={{ fontSize: "0.7rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                          <Check size={12} /> Signed Digitally
                        </span>
                      ) : (
                        <span className="badge badge-secondary" style={{ fontSize: "0.7rem" }}>
                          Unsigned
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Signatory Full Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={signatoryLat.signatoryName || ""}
                        onChange={(e) => setSignatoryLat({
                          ...signatoryLat,
                          signatoryName: e.target.value,
                          signatureText: e.target.value
                        })}
                        placeholder="e.g. Dale Flippo"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Signatory Title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={signatoryLat.signatoryTitle || ""}
                        onChange={(e) => setSignatoryLat({ ...signatoryLat, signatoryTitle: e.target.value })}
                        placeholder="e.g. President / Vice President"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Date Signed</label>
                      <input
                        type="date"
                        className="form-input"
                        value={signatoryLat.date || effectiveDate}
                        onChange={(e) => setSignatoryLat({ ...signatoryLat, date: e.target.value })}
                      />
                    </div>

                    {/* Signature Preview & Action */}
                    <div style={{
                      padding: "0.75rem",
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      border: "1px dashed var(--border-color)",
                      borderRadius: "6px",
                      marginTop: "0.75rem",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", marginBottom: "0.3rem" }}>
                        Digital Signature Preview:
                      </div>
                      <div style={{
                        fontFamily: "'Brush Script MT', 'Dancing Script', 'Segoe Script', 'Great Vibes', cursive",
                        fontSize: "1.8rem",
                        color: "var(--primary)",
                        fontStyle: "italic",
                        minHeight: "42px",
                        lineHeight: 1.2
                      }}>
                        {signatoryLat.signatureText || signatoryLat.signatoryName || "Your Signature Here"}
                      </div>
                      <div style={{ borderBottom: "1px solid var(--border-color)", margin: "4px auto 8px auto", width: "80%" }}></div>

                      {signatoryLat.signatureAdopted ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleClearLatSignature}
                          style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                        >
                          <RotateCcw size={12} /> Clear Signature
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAdoptLatSignature}
                          style={{ fontSize: "0.75rem", padding: "0.3rem 0.8rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                        >
                          <PenTool size={12} /> Adopt & Sign Document
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Partner Signatory */}
                  <div className="card glass-card" style={{ padding: "1.25rem", border: "1px solid var(--border-color)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                      <h4 style={{ margin: 0, color: "var(--primary)", fontSize: "0.9rem", fontWeight: 700 }}>
                        {partnerCompany || "Partner"} Signatory
                      </h4>
                      {signatoryPartner.signatureAdopted ? (
                        <span className="badge badge-success" style={{ fontSize: "0.7rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                          <Check size={12} /> Signed Digitally
                        </span>
                      ) : (
                        <span className="badge badge-secondary" style={{ fontSize: "0.7rem" }}>
                          Unsigned
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Signatory Full Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={signatoryPartner.signatoryName || ""}
                        onChange={(e) => setSignatoryPartner({
                          ...signatoryPartner,
                          signatoryName: e.target.value,
                          signatureText: e.target.value
                        })}
                        placeholder="e.g. Jane Smith"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Signatory Title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={signatoryPartner.signatoryTitle || ""}
                        onChange={(e) => setSignatoryPartner({ ...signatoryPartner, signatoryTitle: e.target.value })}
                        placeholder="e.g. Chief Executive Officer"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Date Signed</label>
                      <input
                        type="date"
                        className="form-input"
                        value={signatoryPartner.date || ""}
                        onChange={(e) => setSignatoryPartner({ ...signatoryPartner, date: e.target.value })}
                      />
                    </div>

                    {/* Signature Preview & Action */}
                    <div style={{
                      padding: "0.75rem",
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      border: "1px dashed var(--border-color)",
                      borderRadius: "6px",
                      marginTop: "0.75rem",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", marginBottom: "0.3rem" }}>
                        Digital Signature Preview:
                      </div>
                      <div style={{
                        fontFamily: "'Brush Script MT', 'Dancing Script', 'Segoe Script', 'Great Vibes', cursive",
                        fontSize: "1.8rem",
                        color: "var(--primary)",
                        fontStyle: "italic",
                        minHeight: "42px",
                        lineHeight: 1.2
                      }}>
                        {signatoryPartner.signatureText || signatoryPartner.signatoryName || "Partner Signature Here"}
                      </div>
                      <div style={{ borderBottom: "1px solid var(--border-color)", margin: "4px auto 8px auto", width: "80%" }}></div>

                      {signatoryPartner.signatureAdopted ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleClearPartnerSignature}
                          style={{ fontSize: "0.75rem", padding: "0.25rem 0.6rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                        >
                          <RotateCcw size={12} /> Clear Signature
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAdoptPartnerSignature}
                          style={{ fontSize: "0.75rem", padding: "0.3rem 0.8rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                        >
                          <PenTool size={12} /> Adopt & Sign Document
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div style={{
          padding: "1rem 1.5rem",
          borderTop: "1px solid var(--border-color)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(10, 12, 18, 0.4)"
        }}>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePrint}
              style={{ padding: "0.45rem 0.9rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.3rem" }}
            >
              <Printer size={15} /> Print / PDF Preview
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleExportWord}
              style={{ padding: "0.45rem 0.9rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.3rem" }}
            >
              <Download size={15} /> Export to Word (.doc)
            </button>
          </div>

          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
              style={{ padding: "0.45rem 1rem", fontSize: "0.85rem" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="partnerAgreementForm"
              className="btn btn-primary"
              disabled={isSaving}
              style={{ padding: "0.45rem 1.25rem", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.4rem" }}
            >
              <Save size={15} /> {isSaving ? "Saving..." : "Save Agreement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
